import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Pagination,
  Chip,
  InputAdornment
} from '@mui/material';
import { 
  Search,
  Refresh,
  FileDownload,
  CalendarToday,
  FilterList,
  Clear
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { WebSocketContext } from '../WebSocketContext';
import config from '../config';
import './MasterNetPositions.css';

const MasterNetPositions = () => {
  const { isDarkMode } = useTheme();
  const { userDetails } = useContext(WebSocketContext);
  
  // State for filters
  const [filters, setFilters] = useState({
    selectedClientId: '',
    selectedSegment: '',
    selectedStockName: '',
    selectedBrokerId: '',
    selectedStatus: '',
    selectedDate: '',
    selectedTimeRange: 'today'
  });

  // State for data
  const [netPositions, setNetPositions] = useState([]);
  const [clients, setClients] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch net positions data
  const fetchNetPositions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}getMasterNetPositions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer 7c85cfc0ad2328331d4784ffedb97565154cd2016ed0b2b6843a53e7f2a2343d`
        },
        body: JSON.stringify({
          ...filters,
          page: currentPage,
          limit: 50
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setNetPositions(data.data || []);
        setTotalPages(data.totalPages || 1);
        setTotalRecords(data.totalRecords || 0);
      }
    } catch (error) {
      console.error('Error fetching net positions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dropdown options (similar to MasterTrades)
  const fetchDropdownOptions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const clientId = localStorage.getItem('userId');
      const role = localStorage.getItem('role');
      
      // Fetch clients based on role
      let clientData = {
        user_id: clientId
      };
      
      if (role === 'BR') {
        clientData.user_type = 'broker';
      } else if (role === 'A') {
        clientData.client_type = 'admin';
      }
      
      const clientResponse = await fetch(`${config.API_BASE_URL}${role === 'BR' ? 'getDownClients' : 'getClientsOnly'}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clientData)
      });
      
      if (clientResponse.ok) {
        const clientData = await clientResponse.json();
        setClients(clientData || []);
      }
      
      // Fetch segments for markets
      const segmentResponse = await fetch(`${config.API_BASE_URL}segments`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (segmentResponse.ok) {
        const segmentData = await segmentResponse.json();
        setMarkets(segmentData || []);
      }
      
      // Fetch brokers (if needed)
      const brokerResponse = await fetch(`${config.API_BASE_URL}getBrokers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: clientId })
      });
      
      if (brokerResponse.ok) {
        const brokerData = await brokerResponse.json();
        setBrokers(brokerData || []);
      }
      
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    }
  };

  // Fetch scripts for selected segment
  const fetchScriptsOfSegment = async (segment) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${config.API_BASE_URL}getScriptsOfSegment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          segment: segment || 'all'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setStocks(data || []);
      }
    } catch (error) {
      console.error('Error fetching scripts:', error);
    }
  };

  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    fetchNetPositions();
  }, [filters, currentPage]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
    
    // Fetch scripts when segment changes
    if (field === 'selectedSegment') {
      fetchScriptsOfSegment(value);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      selectedClientId: '',
      selectedSegment: '',
      selectedStockName: '',
      selectedBrokerId: '',
      selectedStatus: '',
      selectedDate: '',
      selectedTimeRange: 'today'
    });
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleRefresh = () => {
    fetchNetPositions();
  };

  const handleExport = () => {
    // Export functionality
    console.log('Export net positions');
  };

  const getPnLColor = (pnl) => {
    if (pnl > 0) return '#4caf50';
    if (pnl < 0) return '#f44336';
    return '#757575';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#2196f3';
      case 'closed': return '#4caf50';
      case 'partial': return '#ff9800';
      default: return '#757575';
    }
  };

  return (
    <Box className={`master-netpositions-container ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      {/* Desktop Side Panel + Main Content */}
      <Box className="master-desktop-layout">
        {/* Desktop Side Panel Filters - Toggleable */}
        {showFilters && (
          <Box className="master-side-panel">
            <Card className="master-side-panel-card">
              <CardContent className="master-side-panel-content">
                <Typography variant="h6" className="master-side-panel-title">
                  Filters
                </Typography>
                
                <Box className="master-side-panel-filters">
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Client</InputLabel>
                    <Select
                      value={filters.selectedClientId}
                      onChange={(e) => handleFilterChange('selectedClientId', e.target.value)}
                      label="Client"
                    >
                      <MenuItem value="">All Clients</MenuItem>
                      {clients.map((client) => (
                        <MenuItem key={client.clientId || client.id} value={client.clientId || client.id}>
                          {client.clientName || client.name} ({client.clientId || client.id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Segment</InputLabel>
                    <Select
                      value={filters.selectedSegment}
                      onChange={(e) => handleFilterChange('selectedSegment', e.target.value)}
                      label="Segment"
                    >
                      <MenuItem value="">All Segments</MenuItem>
                      {markets.map((market) => (
                        <MenuItem key={market.segment} value={market.segment}>
                          {market.segment}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Stock</InputLabel>
                    <Select
                      value={filters.selectedStockName}
                      onChange={(e) => handleFilterChange('selectedStockName', e.target.value)}
                      label="Stock"
                    >
                      <MenuItem value="">All Stocks</MenuItem>
                      {stocks.map((stock) => (
                        <MenuItem key={stock.instrumentIdentifier || stock.id} value={stock.dispname || stock.name}>
                          {stock.dispname || stock.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Broker</InputLabel>
                    <Select
                      value={filters.selectedBrokerId}
                      onChange={(e) => handleFilterChange('selectedBrokerId', e.target.value)}
                      label="Broker"
                    >
                      <MenuItem value="">All Brokers</MenuItem>
                      {brokers.map((broker) => (
                        <MenuItem key={broker.brokerId || broker.id} value={broker.brokerId || broker.id}>
                          {broker.brokerName || broker.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.selectedStatus}
                      onChange={(e) => handleFilterChange('selectedStatus', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                      <MenuItem value="partial">Partial</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Date"
                    type="date"
                    value={filters.selectedDate}
                    onChange={(e) => handleFilterChange('selectedDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    className="master-side-panel-field"
                  />
                  
                  <FormControl fullWidth size="small" className="master-side-panel-field">
                    <InputLabel>Time Range</InputLabel>
                    <Select
                      value={filters.selectedTimeRange}
                      onChange={(e) => handleFilterChange('selectedTimeRange', e.target.value)}
                      label="Time Range"
                    >
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="yesterday">Yesterday</MenuItem>
                      <MenuItem value="week">This Week</MenuItem>
                      <MenuItem value="month">This Month</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                    className="master-side-panel-clear-btn"
                    fullWidth
                  >
                    Clear Filters
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Main Content */}
        <Box className="master-main-content">
      {/* Header */}
      <Card className="master-header-card">
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" className="master-page-title">
              Master Net Positions
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                className="filter-toggle-btn"
              >
                Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={loading}
                className="refresh-btn"
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExport}
                className="export-btn"
              >
                Export
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters Panel - Compact */}
      {showFilters && (
        <Card className="master-filters-card">
          <CardContent className="master-filters-content">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={filters.selectedClientId}
                    onChange={(e) => handleFilterChange('selectedClientId', e.target.value)}
                    label="Client"
                  >
                    <MenuItem value="">All Clients</MenuItem>
                    {clients.map((client) => (
                      <MenuItem key={client.clientId || client.id} value={client.clientId || client.id}>
                        {client.clientName || client.name} ({client.clientId || client.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Segment</InputLabel>
                  <Select
                    value={filters.selectedSegment}
                    onChange={(e) => handleFilterChange('selectedSegment', e.target.value)}
                    label="Segment"
                  >
                    <MenuItem value="">All Segments</MenuItem>
                    {markets.map((market) => (
                      <MenuItem key={market.segment} value={market.segment}>
                        {market.segment}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Stock</InputLabel>
                  <Select
                    value={filters.selectedStockName}
                    onChange={(e) => handleFilterChange('selectedStockName', e.target.value)}
                    label="Stock"
                  >
                    <MenuItem value="">All Stocks</MenuItem>
                    {stocks.map((stock) => (
                      <MenuItem key={stock.instrumentIdentifier || stock.id} value={stock.dispname || stock.name}>
                        {stock.dispname || stock.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Broker</InputLabel>
                  <Select
                    value={filters.selectedBrokerId}
                    onChange={(e) => handleFilterChange('selectedBrokerId', e.target.value)}
                    label="Broker"
                  >
                    <MenuItem value="">All Brokers</MenuItem>
                    {brokers.map((broker) => (
                      <MenuItem key={broker.brokerId || broker.id} value={broker.brokerId || broker.id}>
                        {broker.brokerName || broker.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.selectedStatus}
                    onChange={(e) => handleFilterChange('selectedStatus', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date"
                  type="date"
                  value={filters.selectedDate}
                  onChange={(e) => handleFilterChange('selectedDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={filters.selectedTimeRange}
                    onChange={(e) => handleFilterChange('selectedTimeRange', e.target.value)}
                    label="Time Range"
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="yesterday">Yesterday</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                    className="clear-filters-btn"
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} className="master-summary-grid">
        <Grid item xs={12} sm={6} md={3}>
          <Card className="master-summary-card">
            <CardContent>
              <Typography variant="h6" className="summary-title">Total Positions</Typography>
              <Typography variant="h4" className="summary-value">
                {totalRecords}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="master-summary-card">
            <CardContent>
              <Typography variant="h6" className="summary-title">Open Positions</Typography>
              <Typography variant="h4" className="summary-value">
                {netPositions.filter(pos => pos.status === 'open').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="master-summary-card">
            <CardContent>
              <Typography variant="h6" className="summary-title">Total P&L</Typography>
              <Typography 
                variant="h4" 
                className="summary-value"
                style={{ color: getPnLColor(netPositions.reduce((sum, pos) => sum + (pos.unrealizedPnL || 0), 0)) }}
              >
                ₹{netPositions.reduce((sum, pos) => sum + (pos.unrealizedPnL || 0), 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className="master-summary-card">
            <CardContent>
              <Typography variant="h6" className="summary-title">Active Clients</Typography>
              <Typography variant="h4" className="summary-value">
                {new Set(netPositions.map(pos => pos.clientId)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Net Positions Table */}
      <Card className="master-data-card">
        <CardContent>
          <TableContainer component={Paper} className="master-table-container">
            <Table className="master-data-table">
              <TableHead>
                <TableRow className="master-table-header">
                  <TableCell>Client</TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell>Exchange</TableCell>
                  <TableCell>Segment</TableCell>
                  <TableCell>Net Qty</TableCell>
                  <TableCell>Avg Buy Price</TableCell>
                  <TableCell>Avg Sell Price</TableCell>
                  <TableCell>LTP</TableCell>
                  <TableCell>Unrealized P&L</TableCell>
                  <TableCell>Realized P&L</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography>Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : netPositions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography>No net positions found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  netPositions.map((position, index) => (
                    <TableRow key={index} className="master-table-row">
                      <TableCell>{position.clientName} ({position.clientId})</TableCell>
                      <TableCell>{position.stockName}</TableCell>
                      <TableCell>{position.exchange}</TableCell>
                      <TableCell>{position.segment}</TableCell>
                      <TableCell 
                        style={{ 
                          color: position.netQty > 0 ? '#4caf50' : position.netQty < 0 ? '#f44336' : '#757575',
                          fontWeight: 'bold'
                        }}
                      >
                        {position.netQty}
                      </TableCell>
                      <TableCell>₹{position.avgBuyPrice?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell>₹{position.avgSellPrice?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell>₹{position.ltp?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell 
                        style={{ 
                          color: getPnLColor(position.unrealizedPnL || 0),
                          fontWeight: 'bold'
                        }}
                      >
                        ₹{(position.unrealizedPnL || 0).toFixed(2)}
                      </TableCell>
                      <TableCell 
                        style={{ 
                          color: getPnLColor(position.realizedPnL || 0),
                          fontWeight: 'bold'
                        }}
                      >
                        ₹{(position.realizedPnL || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={position.status || 'open'} 
                          size="small"
                          style={{ 
                            backgroundColor: getStatusColor(position.status || 'open'),
                            color: 'white'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </CardContent>
      </Card>
        </Box>
      </Box>

    </Box>
  );
};

export default MasterNetPositions;
